import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Users,
  Search,
  Plus,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Bell,
  Check,
  Clock,
  ShoppingBag,
  AlertTriangle,
  ChevronRight
} from "lucide-react";
import api from "@/lib/api";

export default function ClientsPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [pendingReminders, setPendingReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient, setNewClient] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    id_number: "",
    notes: ""
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientsRes, remindersRes] = await Promise.all([
        api.get("/clients"),
        api.get("/reminders/pending")
      ]);
      setClients(clientsRes.data);
      setPendingReminders(remindersRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const searchClients = async () => {
    try {
      const response = await api.get(`/clients?search=${encodeURIComponent(search)}`);
      setClients(response.data);
    } catch (error) {
      toast.error("Erreur de recherche");
    }
  };

  const createClient = async () => {
    if (!newClient.name || !newClient.phone) {
      toast.error("Nom et téléphone sont requis");
      return;
    }
    
    try {
      await api.post("/clients", newClient);
      toast.success("Client créé avec succès!");
      setShowAddClient(false);
      setNewClient({ name: "", phone: "", email: "", address: "", id_number: "", notes: "" });
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la création");
    }
  };

  const completeReminder = async (reminderId) => {
    try {
      await api.put(`/reminders/${reminderId}/complete`);
      toast.success("Rappel marqué comme terminé");
      loadData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const filteredClients = search
    ? clients.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.phone.includes(search) ||
        c.email?.toLowerCase().includes(search.toLowerCase())
      )
    : clients;

  return (
    <Layout>
      <div className="p-4 md:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
              <Users className="w-6 h-6 text-primary" />
              Gestion des Clients
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {clients.length} client(s) enregistré(s)
            </p>
          </div>
          
          <Dialog open={showAddClient} onOpenChange={setShowAddClient}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-black" data-testid="btn-add-client">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Client
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Ajouter un Client</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="client-name">Nom *</Label>
                  <Input
                    id="client-name"
                    value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    placeholder="Nom complet"
                    data-testid="input-new-client-name"
                  />
                </div>
                <div>
                  <Label htmlFor="client-phone">Téléphone *</Label>
                  <Input
                    id="client-phone"
                    value={newClient.phone}
                    onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                    placeholder="Numéro de téléphone"
                    data-testid="input-new-client-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="client-email">Email</Label>
                  <Input
                    id="client-email"
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                    placeholder="Email (optionnel)"
                  />
                </div>
                <div>
                  <Label htmlFor="client-address">Adresse</Label>
                  <Input
                    id="client-address"
                    value={newClient.address}
                    onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                    placeholder="Adresse"
                  />
                </div>
                <div>
                  <Label htmlFor="client-notes">Notes</Label>
                  <Textarea
                    id="client-notes"
                    value={newClient.notes}
                    onChange={(e) => setNewClient({ ...newClient, notes: e.target.value })}
                    placeholder="Notes..."
                    rows={2}
                  />
                </div>
                <Button
                  onClick={createClient}
                  className="w-full bg-primary text-black"
                  data-testid="btn-save-client"
                >
                  Enregistrer
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Pending Reminders */}
        {pendingReminders.length > 0 && (
          <Card className="border-orange-500/30 bg-orange-500/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Bell className="w-4 h-4 text-orange-500" />
                Rappels en attente ({pendingReminders.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {pendingReminders.slice(0, 5).map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-background rounded-lg border border-border"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${
                        item.reminder.reminder_type === 'call_48h' 
                          ? 'bg-blue-500/20' 
                          : 'bg-green-500/20'
                      }`}>
                        {item.reminder.reminder_type === 'call_48h' ? (
                          <Phone className="w-4 h-4 text-blue-500" />
                        ) : (
                          <Calendar className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{item.client_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.reminder.reminder_type === 'call_48h' 
                            ? 'Appel 48h après achat' 
                            : 'Rappel révision mensuelle'}
                        </p>
                        <p className="text-xs text-muted-foreground">{item.client_phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-orange-500 border-orange-500/30">
                        <Clock className="w-3 h-3 mr-1" />
                        {item.reminder.due_date}
                      </Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => completeReminder(item.reminder.id)}
                        data-testid={`btn-complete-reminder-${index}`}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un client..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchClients()}
              className="pl-10"
              data-testid="input-search-clients"
            />
          </div>
          <Button variant="outline" onClick={searchClients}>
            Rechercher
          </Button>
        </div>

        {/* Clients List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-pulse text-muted-foreground">Chargement...</div>
          </div>
        ) : filteredClients.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucun client trouvé</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredClients.map((client) => (
              <Card
                key={client.id}
                className="hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/clients/${client.client_id}`)}
                data-testid={`client-card-${client.client_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium">{client.name}</h3>
                      <p className="text-xs text-muted-foreground font-mono">{client.client_id}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                  
                  <div className="mt-3 space-y-1">
                    {client.phone && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Phone className="w-3 h-3" />
                        {client.phone}
                      </div>
                    )}
                    {client.email && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Mail className="w-3 h-3" />
                        {client.email}
                      </div>
                    )}
                    {client.address && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="w-3 h-3" />
                        {client.address}
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-3 flex items-center gap-2">
                    {client.purchases?.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        <ShoppingBag className="w-3 h-3 mr-1" />
                        {client.purchases.length} achat(s)
                      </Badge>
                    )}
                    {client.reminders?.filter(r => !r.completed).length > 0 && (
                      <Badge variant="outline" className="text-xs text-orange-500 border-orange-500/30">
                        <Bell className="w-3 h-3 mr-1" />
                        {client.reminders.filter(r => !r.completed).length}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
